import { Fragment } from 'react';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { Field, LinkField, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/component-props';
import {
  SITE_MASTER_COLORS,
  StSiteMasterHeroContainer,
  StSiteMasterHeroWrapper,
  StSiteMasterHeroDetails,
  StSiteMasterHeroImage,
  StSiteMasterHeroLeft,
  StSiteMasterHeroTagList,
  StSiteMasterChip,
} from 'components/SiteMaster/SiteMaster.styles';
import {
  getFieldValue,
  getMultilistDisplayFields,
  extractNumberFromString,
  useSiteMasterRouteFields,
  useSiteRecruitingStudiesProps,
} from 'components/SiteMaster/utils';
import { mergeTag } from 'lib/utils/common';
import UiBox from 'components/shared/Box';
import ButtonSecondary from 'components/shared/Button/SecondaryButton';
import UiHeading from 'components/shared/Heading';
import CustomNextImage from 'components/shared/CustomNextImage/CustomNextImage';

type SiteHeroBannerProps = ComponentProps & {
  fields: {
    backGroundColor?: Field<string>;
    callSiteText?: Field<string>;
    saveSiteText?: Field<string>;
    establishmentYearFormat?: Field<string>;
    enableBreadcrumb?: Field<boolean>;
    breadcrumbLabel?: Field<string>;
    breadcrumbUrl?: LinkField;
  };
};

const formatHeroEstablishmentYear = (rawYear?: string, format?: string): string => {
  const yearFormat = format?.trim() ?? '';
  const year = extractNumberFromString(rawYear);
  if (!yearFormat || year === null || year < 1000 || year > 9999) {
    return '';
  }
  return mergeTag(yearFormat, { year: String(year) });
};

const SiteHeroBanner = (props: SiteHeroBannerProps): JSX.Element => {
  const theme = useTheme();
  const routeFields = useSiteMasterRouteFields();
  const { sitecoreContext } = useSitecoreContext();
  const siteSettings: any = sitecoreContext?.siteSettings;
  // Single owner: SiteRecruitingStudies getStaticProps
  const { hasRecruitingStudies } = useSiteRecruitingStudiesProps();
  const { fields } = props;
  const siteName = getFieldValue(routeFields.siteName);
  const sitePhone = getFieldValue(routeFields.sitePhone);
  const hasHeroImage = [
    routeFields.siteImageDesktop,
    routeFields.siteImageTablet,
    routeFields.siteImageMobile,
    routeFields.siteImage,
  ].some((field) => field?.value?.src);
  const languageFields = getMultilistDisplayFields(routeFields.siteSupportedLanguages);
  const establishmentYear = formatHeroEstablishmentYear(
    getFieldValue(routeFields.siteEstablishmentYearText),
    getFieldValue(fields.establishmentYearFormat)
  );

  const heroTags: { key: string; content: JSX.Element }[] = [];

  if (getFieldValue(routeFields.siteType)) {
    heroTags.push({ key: 'siteType', content: <Text field={routeFields.siteType} /> });
  }
  if (establishmentYear) {
    heroTags.push({ key: 'establishmentYear', content: <>{establishmentYear}</> });
  }
  if (getFieldValue(routeFields.sitePhase)) {
    heroTags.push({ key: 'sitePhase', content: <Text field={routeFields.sitePhase} /> });
  }
  if (languageFields.length) {
    heroTags.push({
      key: 'siteSupportedLanguages',
      content: (
        <>
          {languageFields.map((languageField, index) => (
            <Fragment key={`${getFieldValue(languageField)}-${index}`}>
              {index > 0 && ' / '}
              <Text field={languageField} />
            </Fragment>
          ))}
        </>
      ),
    });
  }

  const heroBackground =
    getFieldValue(fields.backGroundColor) ||
    theme.palette.themeColor?.main ||
    SITE_MASTER_COLORS.teal;

  const noStudiesAvailableText = getFieldValue(siteSettings?.noStudiesAvailableText);
  const showNoStudiesMessage = !hasRecruitingStudies && Boolean(noStudiesAvailableText);

  return (
    <StSiteMasterHeroContainer sx={{ background: heroBackground }}>
      <StSiteMasterHeroWrapper>
        <StSiteMasterHeroLeft>
          <StSiteMasterHeroDetails>
            <UiHeading
              size="h1"
              color="white"
              isChildren
              className="site-hero-title"
              styles={{
                fontWeight: 400,
                fontSize: '48px',
                lineHeight: 'normal',
              }}
            >
              <Text field={routeFields.siteName} defaultValue={siteName} />
            </UiHeading>
            {heroTags.length > 0 && (
              <StSiteMasterHeroTagList>
                {heroTags.map((tag) => (
                  <StSiteMasterChip
                    key={tag.key}
                    sx={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  >
                    {tag.content}
                  </StSiteMasterChip>
                ))}
              </StSiteMasterHeroTagList>
            )}
            {showNoStudiesMessage ? (
              <Typography
                sx={{
                  color: theme.palette.common.white,
                  fontWeight: 700,
                  fontSize: '19px',
                  lineHeight: 1.4,
                }}
              >
                <Text field={siteSettings?.noStudiesAvailableText} />
              </Typography>
            ) : null}
            <UiBox styles={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
              <ButtonSecondary iconRight={<StarBorderOutlinedIcon />}>
                <Text field={fields.saveSiteText} />
              </ButtonSecondary>
              {sitePhone && (
                <ButtonSecondary
                  iconRight={<PhoneOutlinedIcon />}
                  href={`tel:${sitePhone}`}
                  target="_blank"
                >
                  <Text field={fields.callSiteText} />
                </ButtonSecondary>
              )}
            </UiBox>
          </StSiteMasterHeroDetails>
        </StSiteMasterHeroLeft>
        {hasHeroImage && (
          <StSiteMasterHeroImage>
            <CustomNextImage
              mobileImg={routeFields.siteImageMobile}
              tabletImg={routeFields.siteImageTablet}
              desktopImg={routeFields.siteImageDesktop ?? routeFields.siteImage}
              objectFit="cover"
              objectPosition="center"
              priority
              ariaLabel={siteName}
            />
          </StSiteMasterHeroImage>
        )}
      </StSiteMasterHeroWrapper>
    </StSiteMasterHeroContainer>
  );
};

export default SiteHeroBanner;
